/**
 * Scheduling's complete method/type/schema catalog lives in the shared package so HTTP,
 * backend and agent callers can use the same contract without importing backend code.
 * `Scheduling` is flat: each method takes (context, request). Channel blocks below are
 * SETTINGS, not nested service interfaces. No runtime implementation is selected here.
 */
export * from "@/shared/scheduling/contract";

import type { VoiceAppointmentSmsPolicy } from "@/configuration/voice-appointment-sms";
import type { CustomerSmsRoutingConfig } from "@/customer-interface/sms-routing";
import type { BookedAppointment, RecommendationType } from "@/shared";
import type { BookAppointmentSlot } from "@/shared/agent-api/requests";
import type { AppointmentType } from "@/shared/models/appointment";
import type { CareMode, Channel, SchedulingPresentation, SchedulingSlotKind } from "@/shared/scheduling/contract";
import type { VoiceAgentRuntime } from "@/shared/voice-runtime";
import type { AppointmentActionPolicy } from "@/src/lib/comms/scheduling/appointment-action-policy";
import type { AdvancedMdSchedulingAvailability } from "@/src/models/advancedmd-scheduling-availability";
import type { BookedAppointmentWithLocation } from "@/src/models/appointment";
import type { EcwSchedulingVisitTypeBinding } from "@/src/models/ecw-scheduling-visit-type-binding";

/** Pure descriptions of authoritative facts; cannot change permissions, membership or allocations. */
export interface SchedulingPresentationPolicy {
  describeService(facts: {
    appointmentType: AppointmentType;
    appointmentPolicyTags: readonly string[];
  }): { serviceLabel: string; targetKind: SchedulingSlotKind } | undefined;
  describeRescheduleUnit(policyId: string): SchedulingPresentation | undefined;
}

export enum SchedulingSmsAvailabilityStrategy {
  AgenticProvider = "agentic_provider",
  LocationResource = "location_resource",
}

export enum SchedulingSmsEligibilityStrategy {
  Agentic = "agentic",
}

/** Pure customer decisions; shared operations own identity, eligibility and mutation guards. */
export interface SchedulingSmsReschedulingPolicy {
  readonly maximumOffers: number;
  allowsSource(source: Pick<BookedAppointment, "appointmentDatetime">, now: Date): boolean;
  allowsProvider(source: Pick<BookedAppointment, "practitionerId">, practitionerId: string): boolean;
  rankingCriteria(source: Pick<BookedAppointment, "appointmentDatetime" | "practitionerDisplayName">): string;
  allowsTarget(
    source: Pick<BookedAppointment, "appointmentDatetime" | "practitionerId">,
    target: Pick<BookAppointmentSlot, "start" | "practitionerId">
  ): boolean;
}

export enum SchedulingCancellationStrategy {
  Direct = "direct",
  Staff = "staff",
}

export interface SchedulingOptionSearchConfig {
  /** Safe only for a deterministic, post-filtered chronological prefix. */
  readonly progressiveChronologicalWindows: boolean;
  /** Rank complete bounded near-term windows before searching the remainder. */
  readonly progressivePolicyRankedWindows: boolean;
  /** Search linked-unit replacements in complete bounded chronological windows. */
  readonly progressiveLinkedUnitWindows: boolean;
  /**
   * Longest a caller's turn may spend on Voice availability searches, preparatory reads included, counted from
   * the turn's first page when the agent reports it. When it runs out, the page returns an explicit incomplete
   * result: a first page with no times and nothing to continue, a continued page with its cursor unchanged.
   * Read-only searches only; booking writes keep their own deadlines. Absent means no limit.
   */
  readonly searchBudgetMs?: number;
}

export interface SchedulingResourceBookingChoice {
  readonly id: string;
  readonly appointmentType: AppointmentType;
  readonly careMode: CareMode;
}

export interface SchedulingResourceBookingPolicy {
  readonly appointmentType: AppointmentType;
  readonly careMode: CareMode;
  /** Omitted for a resource workflow with only one caller-selectable service. */
  readonly choices?: readonly SchedulingResourceBookingChoice[];
}

/** A single referral-matched region; customers needing multiple regions retain their own policy. */
export interface SchedulingReferralRegionConfig {
  readonly regionId: string;
  readonly excludedRegionId: string;
  /** A referral matches when its receiving provider OR receiving location is in these lists. */
  readonly providerNpis: readonly string[];
  readonly locationEhrIds: readonly string[];
}

/** Spacing between one patient's appointments of a type, enforced from that patient's booked history. */
export type SchedulingAppointmentSpacingRule = {
  readonly appointmentType: AppointmentType;
  /**
   * Calendar days after the patient's previous appointment of this type before another may start. Any
   * past one that wasn't cancelled or missed counts, whether checked out or still unresolved.
   */
  readonly minimumDaysAfterPrevious: number;
  /** Refuses another while one of this type is still booked, counting unresolved source statuses. */
  readonly singleBooked: boolean;
} & (
  | { readonly appointmentTypeChoiceId?: undefined; readonly ehrVisitTypes?: undefined }
  | {
      /**
       * Spaces only the group's own visit of the type, such as Axia's screening mammogram among its procedures:
       * a request names it by this choice ID, and a booked appointment is it when its EHR visit type is one of
       * `ehrVisitTypes` (exact, ignoring case). Other appointments of the type don't count.
       */
      readonly appointmentTypeChoiceId: string;
      readonly ehrVisitTypes: readonly [string, ...string[]];
    }
);

/** A facility's eCW scheduling resource for some eCW visit types, e.g. its mammography machine. */
export interface EcwResourceCalendar {
  /** The eCW visit types (template codes) booked on the resource's calendar, compared ignoring case. */
  readonly ecwVisitTypeCodes: readonly [string, ...string[]];
  /** The facility's eCW ID (the location's `ehrId`). */
  readonly facilityEhrId: string;
  /** The eCW resource whose calendar and templates hold those visit types' times. */
  readonly resourceEhrId: string;
}

/** A group's own visit: a generic appointment type plus the group's choice ID for it (`appointmentTypeChoiceId`). */
export interface SchedulingNamedVisit {
  readonly appointmentType: AppointmentType;
  readonly appointmentTypeChoiceId: string;
}

/** The patient's orders that name a primary-provider visit's provider, e.g. a screening mammogram's order. */
export interface SchedulingVisitOrderMatch {
  /** An order counts when its name contains every term of one entry, ignoring case, e.g. a CPT code. */
  readonly orderNameTerms: readonly (readonly string[])[];
  /** Orders placed more than this many days ago don't count. */
  readonly maxOrderAgeDays: number;
}

/** Note text for a moved appointment; the other appointment's date and time follow each one. */
export interface SchedulingRescheduleNotes {
  readonly replacement: string;
  readonly original: string;
}

export enum SchedulingHistoryLocationScope {
  Any = "any",
  ReceivingLocations = "receiving_locations",
}

/** Which attended visits make a patient established. Absent: receiving locations, last 3 years. */
export interface SchedulingEstablishedPatientHistoryPolicy {
  /** null counts every attended visit the source has recorded; requires complete appointment history. */
  readonly lookbackYears: number | null;
  readonly locationScope: SchedulingHistoryLocationScope;
}

/** Pure decisions over a fresh appointment and, when required, complete patient history. */
export interface SchedulingAppointmentCancellationPolicy {
  readonly requiresCompleteHistory: boolean;
  allows(input: {
    appointment: BookedAppointmentWithLocation;
    history: readonly BookedAppointmentWithLocation[];
    now: Date;
  }): boolean;
}

/** Pure settings only. Presence does not enable a channel or supply a Scheduling implementation. */
export interface SchedulingCustomerConfig {
  /** Backend-only AdvancedMD settings; never part of the public Scheduling request/response contract. */
  readonly advancedMd?: {
    readonly availability: AdvancedMdSchedulingAvailability;
  };
  /** Backend-only eCW settings; never part of the public Scheduling request/response contract. */
  readonly ecw?: {
    readonly availabilityVisitBindings?: readonly EcwSchedulingVisitTypeBinding[];
    /**
     * Calendar months ahead of today a WebECW availability search may reach, for bookings and moves; the
     * scheduling-index planner uses the same horizon. Absent means 6 months. Only WebECW honors it: Cobalt keeps
     * its own 6-month cap and Athena doesn't read it. A search limit, not a booking deadline, and no promise that
     * eCW has calendar data that far ahead.
     */
    readonly availabilitySearchHorizonMonths?: number;
    /**
     * eCW visit types booked on an eCW resource's own calendar, such as a mammography machine's, instead of the
     * provider's. At a listed facility, availability for a bound template code reads that resource's templates, and
     * the booking writes the resource with the searched provider as the appointment's provider. Other facilities
     * offer no times for the code. One resource per facility and code.
     *
     * Interim, not an onboarding pattern: the scheduling resource catalog doesn't hold an eCW resource's facility
     * yet (only Solv lists resource targets), so this pairs them in code. Moving them there is TODO(VAL-3834).
     */
    readonly resourceCalendars?: readonly EcwResourceCalendar[];
    /** Read WebECW insurance when referral and patient coverage are absent, for these exact groups. */
    readonly patientCoverageFallbackGroupIds?: readonly string[];
    /** Written to the notes of each appointment Voice books, so staff can find Valerie's bookings. */
    readonly bookingAttributionNote?: string;
    /** Added to those notes when the caller agreed to book as self-pay. */
    readonly selfPayBookingNote?: string;
    /** Adds the caller-reported payer and plan to those notes; Valerie writes no coverage. */
    readonly reportedInsuranceInBookingNote?: boolean;
    /** Status writes append to an appointment's notes, and a move's replacement starts from its source's. */
    readonly preserveAppointmentNotes?: boolean;
    /**
     * Reads keep visits whose eCW type reads as unknown instead of dropping them: history reads count them,
     * and upcoming reads return them read-only, so the scheduling policy's tags can route the caller.
     */
    readonly keepUnknownTypeVisits?: boolean;
    /**
     * On an index-only availability route, a provider whose calendar the scheduling index can't serve (its weeks
     * are missing, stale or unavailable) adds no times, like a provider eCW returns no calendar data for: the search
     * offers the other providers' times and goes to staff only when none has any. Absent means that read fails the
     * whole search.
     */
    readonly skipProvidersWithoutIndexedCoverage?: boolean;
  };
  /** Replace-and-retire moves write these notes, each followed by the other appointment's date and time. */
  readonly rescheduleNotes?: SchedulingRescheduleNotes;
  /** New bookings and reschedules of a spaced type; a move never counts the appointment it moves. */
  readonly appointmentSpacing?: readonly SchedulingAppointmentSpacingRule[];
  /** Exact groups using the mock scheduling source; does not enable any workflow. */
  readonly mockGroupIds?: readonly string[];
  readonly presentation?: SchedulingPresentationPolicy;
  /** Explicit per-channel cohorts for common booked reads; absent means the current path. */
  readonly appointmentQuery?: {
    readonly enabledGroupIds: Readonly<Partial<Record<Channel, readonly string[]>>>;
  };
  /** Explicit per-channel cohorts for the common location query; absent means the current path. */
  readonly locationQuery?: {
    readonly enabledGroupIds: Readonly<Partial<Record<Channel, readonly string[]>>>;
  };
  /** Omitted for routing-only customers; existing scheduling policy selection remains unchanged. */
  readonly policy?: {
    readonly scheduleConfigPath: string | null;
    /** Types returned in scheduling appointment lists. Omitted means no type filter; history reads retain all types. */
    readonly supportedAppointmentTypes?: readonly AppointmentType[];
    /** Structured action policy for workflows without a provider rules document. */
    readonly appointmentActions?: AppointmentActionPolicy;
    /** A successful verdict also requires approved database-owned provider/location membership. */
    readonly cancellation?: SchedulingAppointmentCancellationPolicy;
    readonly appointmentIntakeVoiceRuntimes?: readonly VoiceAgentRuntime[];
    /** Deterministic prior-provider filtering, applied to fresh and reused eligibility results. */
    readonly requireSeenProviderFor?: readonly RecommendationType[];
    readonly establishedPatientHistory?: SchedulingEstablishedPatientHistoryPolicy;
    readonly linkBookingToReferral?: true;
  };
  readonly voice?: {
    readonly appointmentNotifications?: VoiceAppointmentSmsPolicy;
    /** Initial options/booking may use confirmed demographics when the source supports local intake. */
    readonly allowIdentityOnlyBooking?: true;
    /** Source-backed clinic resources; preserves the selected resource/site for a single reschedule. */
    readonly resourceBooking?: SchedulingResourceBookingPolicy;
    /** Eligible groups for explicit common routes. Live tenancy also requires the runtime rollout switch. */
    readonly workflowEnabledGroupIds?: readonly string[];
    readonly cancellation?: SchedulingCancellationStrategy;
    /**
     * The group's own visits booked under the patient's chart primary provider instead of a provider the caller
     * picks, for established patients only, and only when the request names the visit by its choice ID (e.g.
     * Axia's screening mammogram). Their eligibility keeps the history, age and clinical checks and replaces only
     * the provider pick. Where their times are read and what's written is separate (`ecw.resourceCalendars`).
     */
    readonly primaryProviderVisits?: readonly SchedulingNamedVisit[];
    /**
     * For a `primaryProviderVisits` visit, keyed by its choice ID, the open orders whose ordering provider goes on
     * the appointment instead: the newest matching one's, when the line books them. The chart's primary provider
     * goes on it only when a complete read finds no matching order. An order list that can't be read in full, or a
     * matching order whose provider the line doesn't book, goes to staff.
     */
    readonly visitOrders?: Readonly<Partial<Record<string, SchedulingVisitOrderMatch>>>;
    readonly optionSearch: SchedulingOptionSearchConfig;
  };
  readonly sms?: {
    /** Inbound lines, outbound senders and clinic phones; independent of operation rollout. */
    readonly routing?: CustomerSmsRoutingConfig;
    /** Implementation selection; serving still requires the explicit operation cohort below. */
    readonly eligibility?: SchedulingSmsEligibilityStrategy;
    /** Outer SMS tools only; missing cohorts retain their existing implementation. */
    readonly eligibilityEnabledGroupIds?: readonly string[];
    /** Private provider-ranking implementation only; does not enable the recommendations tool. */
    readonly availability?: SchedulingSmsAvailabilityStrategy;
    /** Outer SMS recommendations only; independent of Voice and booking rollout. */
    readonly availabilityEnabledGroupIds?: readonly string[];
    /** Confirmed SMS booking; requires common recommendations/private selections first. */
    readonly bookingEnabledGroupIds?: readonly string[];
    /** Close initial/follow-up SMS workflows after a successful common booking tool result. */
    readonly bookingCompletesWorkflow?: boolean;
    readonly rescheduling?: SchedulingSmsReschedulingPolicy;
    /** Selected-appointment search and confirmed rescheduling are enabled together. */
    readonly reschedulingEnabledGroupIds?: readonly string[];
    /** Optional customer policy; selecting SMS engines does not require referral-region narrowing. */
    readonly referralRegion?: SchedulingReferralRegionConfig;
  };
}
